import { useCallback, useState } from 'react';
import { Profile, SearchFilters, SearchState } from '../types';

export const useSearch = () => {
  const [searchState, setSearchState] = useState<SearchState>({
    profiles: [],
    loading: false,
    hasSearched: false,
    filters: {
      selectedRoles: [],
      selectedSkills: [],
      industryFilter: '',
      rolesSearch: '',
      skillsSearch: '',
    },
  });

  const updateFilters = useCallback((newFilters: Partial<SearchFilters>) => {
    setSearchState(prev => ({
      ...prev,
      filters: { ...prev.filters, ...newFilters },
    }));
  }, []);

  const fetchProfiles = useCallback(async (
    currentFilters: SearchFilters,
    isStaff: boolean = false
  ) => {
    setSearchState(prev => ({ ...prev, loading: true, hasSearched: true }));

    try {
      // Build query parameters from filters
      const params = new URLSearchParams();
      if (currentFilters.selectedRoles.length > 0) {
        params.append('roles', currentFilters.selectedRoles.join(','));
      }
      if (currentFilters.selectedSkills.length > 0) {
        params.append('skills', currentFilters.selectedSkills.join(','));
      }
      if (currentFilters.industryFilter) {
        params.append('industry', currentFilters.industryFilter);
      }
      if (currentFilters.rolesSearch) {
        params.append('rolesSearch', currentFilters.rolesSearch);
      }
      if (currentFilters.skillsSearch) {
        params.append('skillsSearch', currentFilters.skillsSearch);
      }

      const response = await fetch(`/api/${isStaff ? 'staff' : 'profiles'}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      // Apply in-memory filtering on real staff data
      const staff: any[] = Array.isArray(data?.profiles) ? data.profiles : Array.isArray(data) ? data : [];
      const lc = (s: string | undefined | null) => (s ?? '').toString().toLowerCase();

      const filtered = staff.filter((p: any) => {
        // Roles exact match (against roles in first experience by title)
        const rolesSelected = currentFilters.selectedRoles || [];
        const hasSelectedRoles = rolesSelected.length === 0 || rolesSelected.some(r => {
          const target = (p.experiences?.[0]?.roles || []) as any[];
          return target.some(rr => lc(rr?.title) === lc(r));
        });

        // Skills exact match (against any role skills)
        const skillsSelected = currentFilters.selectedSkills || [];
        const hasSelectedSkills = skillsSelected.length === 0 || skillsSelected.some(s => {
          const target = (p.experiences || []) as any[];
          return target.some(exp => (exp.roles || []).some((role: any) => (role.skills || []).some((sk: string) => lc(sk) === lc(s))));
        });

        // Roles search (contains on role title)
        const roleSearch = lc(currentFilters.rolesSearch);
        const matchesRoleSearch = roleSearch.length === 0 || ((p.experiences?.[0]?.roles || []) as any[]).some(rr => lc(rr?.title).includes(roleSearch));

        // Skills search (contains on any role skill)
        const skillSearch = lc(currentFilters.skillsSearch);
        const matchesSkillSearch = skillSearch.length === 0 || (p.experiences || []).some((exp: any) => (exp.roles || []).some((role: any) => (role.skills || []).some((sk: string) => lc(sk).includes(skillSearch))));

        // Industry filter (if present, compare against p.industry or p.industries)
        const industry = lc(currentFilters.industryFilter);
        const matchesIndustry = industry.length === 0 || lc((p as any).industry).includes(industry) || ((p as any).industries || []).some((ind: string) => lc(ind).includes(industry));

        return hasSelectedRoles && hasSelectedSkills && matchesRoleSearch && matchesSkillSearch && matchesIndustry;
      });

      // Transform filtered data to match our Profile interface
      const transformedProfiles: Profile[] = filtered.map((profile: any, index: number) => {
        try {
          // Calculate total experience from all experiences
          const totalExperienceMonths = profile.experiences?.reduce((total: number, exp: any) =>
            total + (exp.totalDurationMonths || 0), 0) || 0;

          // Get the most recent role from the first experience
          const currentRole = profile.experiences?.[0]?.roles?.[0];

          // Get all unique skills from all experiences and roles
          const allSkills = new Set<string>();
          profile.experiences?.forEach((exp: any) => {
            exp.roles?.forEach((role: any) => {
              role.skills?.forEach((skill: string) => {
                if (skill && typeof skill === 'string') {
                  allSkills.add(skill);
                }
              });
            });
          });

          // Fallback skills if none found
          const skills = Array.from(allSkills).slice(0, 6);
          if (skills.length === 0) {
            skills.push('JavaScript', 'React', 'Node.js');
          }

          return {
            id: profile.id || `profile-${index}`,
            title: profile.title || currentRole?.title || 'Software Engineer',
            experience: `${Math.round(totalExperienceMonths / 12)} years of experience`,
            verification: 'Verified by DreamDev',
            skills: skills,
            industry: (profile.industry as string) || (Array.isArray(profile.industries) ? profile.industries[0] : '') || 'Technology',
            industries: profile.industries,
            candidateName: profile.candidateName,
            profileUrl: profile.profileUrl,
            summary: profile.summary,
            email: profile.email,
            phone: profile.phone,
            linkedInLink: profile.linkedInLink,
            experiences: profile.experiences || [],
            anonymizedProfile: profile.anonymizedProfile,
            dayRate: profile.dayRate,
            dayRateCurrency: profile.dayRateCurrency,
          };
        } catch (error) {
          console.error('Error transforming profile:', profile, error);
          // Return a fallback profile
          return {
            id: profile.id || `profile-${index}`,
            title: profile.title || 'Software Engineer',
            experience: '0 years of experience',
            verification: 'Verified by DreamDev',
            skills: ['JavaScript', 'React', 'Node.js'],
            industry: (profile.industry as string) || (Array.isArray(profile.industries) ? profile.industries[0] : '') || 'Technology',
            candidateName: profile.candidateName,
            profileUrl: profile.profileUrl,
            summary: profile.summary,
            email: profile.email,
            phone: profile.phone,
            linkedInLink: profile.linkedInLink,
            experiences: profile.experiences || [],
            anonymizedProfile: profile.anonymizedProfile,
            dayRate: profile.dayRate,
            dayRateCurrency: profile.dayRateCurrency,
          };
        }
      }) || [];

      setSearchState(prev => ({ ...prev, profiles: transformedProfiles }));
    } catch (error) {
      console.error('Error fetching profiles:', error);
      setSearchState(prev => ({ ...prev, profiles: [] }));
    } finally {
      setSearchState(prev => ({ ...prev, loading: false }));
    }
  }, []);

  const clearAll = useCallback(() => {
    setSearchState(prev => ({
      ...prev,
      filters: {
        selectedRoles: [],
        selectedSkills: [],
        industryFilter: '',
        rolesSearch: '',
        skillsSearch: '',
      },
      profiles: [],
      hasSearched: false,
    }));
  }, []);

  return {
    ...searchState,
    updateFilters,
    fetchProfiles,
    clearAll,
  };
};
